import { request } from '../api/client'

/**
 * The portal session and the customer context it grants.
 *
 * Authentication is a Secure, HttpOnly session cookie with CSRF. No token is
 * placed in `localStorage` or `sessionStorage`, so there is nothing for an XSS
 * to exfiltrate and signing out is something the server can actually enforce.
 *
 * A user may act for more than one customer — a consultancy managing two
 * clients is a real case — so the session returns memberships rather than a
 * single customer and the portal asks which one to work in. Silently picking
 * the first would work right up until somebody acquired a second, and then
 * would quietly act on the wrong company's money.
 */

export type CustomerRole = 'owner' | 'admin' | 'finance' | 'developer' | 'viewer'

/** The two Client Portals. See `src/portalMode.ts`. */
export type PortalMode = 'service' | 'professional'

export interface Membership {
  id: string
  role: CustomerRole
  /** What this role may do, as the server computed it. */
  capabilities: string[]
  customer: {
    id: string
    name: string
    slug: string
    status: 'active' | 'suspended' | 'closed'
    /** Which portal this account is shown. Presentation only — it grants
     *  nothing and gates nothing; see `portalMode`. Optional so a portal build
     *  pointed at an older server falls back to the developer portal rather
     *  than hiding surfaces that server still expects to be there. */
    portal_mode?: PortalMode
  }
}

export interface ClientSession {
  user: {
    id: string
    /** Masked. The portal's authentication identity, shown back so somebody
     *  can confirm which number they are signed in as. */
    mobile: string | null
    /** Optional contact metadata. Not an identity, and absent for most
     *  customer people, who never had one. */
    email: string | null
    full_name: string
  }
  memberships: Membership[]
}

export const SESSION_QUERY_KEY = ['client', 'session'] as const

export function fetchSession(signal?: AbortSignal): Promise<ClientSession> {
  return request<ClientSession>('/auth/session/', signal ? { signal } : {})
}

export function logout(): Promise<void> {
  return request<void>('/auth/logout/', { method: 'POST', body: {} })
}

/**
 * Whether the current role permits an action.
 *
 * The server enforces this regardless; the portal consults it only to avoid
 * offering a button that would be refused. Capabilities come from the server
 * rather than being duplicated here, so the two cannot drift.
 */
export function can(membership: Membership | null, capability: string): boolean {
  return membership?.capabilities.includes(capability) ?? false
}
